<template>
    <div
        class="gst-rate-text-cell"
        @keydown.stop>
        <v-text-field
            :model-value="value"
            :name="params.colDef.field"
            :aria-label="params.colDef.headerName"
            :disabled="!editable"
            :clearable="editable"
            :persistent-clear="editable"
            :error="invalid"
            :aria-invalid="invalid || undefined"
            hide-details
            density="compact"
            color="primary"
            variant="outlined"
            class="gst-input--primary gst-input"
            @update:model-value="onInputDo" />
    </div>
</template>

<script>
/**
 * A rate field of a grid of rows not saved yet (the Add a Campaign included hotels' Sellable Rates
 * and VIP Rate): free text, kept as typed. Each change, the "x" included (blank text), goes to the
 * grid's `context.updateRow`, which saves it to the row at once. Where the row's rates don't apply
 * (`ratesEditable` is false) the field is disabled, with no "x".
 *
 * The grid shows each row anew on every change of the form. The field keeps its text here, and
 * refresh() tells AG Grid to keep this cell rather than draw a new one, so the focus and the caret
 * stay while typing. AG Grid refreshes a cell only when its own value changes, so the cell also
 * follows its row: a type change enables or disables the rates of a row whose text stays blank.
 * The keys typed stay in the field: the grid would take the arrows, Home and End to move between
 * cells, and keep Ctrl or Cmd + A from selecting the field's text.
 *
 * A rate the form found wrong on Save (the row's `invalidRates`, by field) is framed red, with no
 * text under it: the section says what is wrong once, under the grid. The frame follows the row as
 * the editable state does, so it goes as soon as the text typed is right, the focus kept.
 */
export default {
    name: 'RateTextCellRenderer',
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            value: '',
            editable: false,
            invalid: false,
        };
    },
    methods: {
        // AG Grid's call when the cell's value changes: true keeps this cell.
        refresh(params) {
            this.show(params);

            return true;
        },
        show({ value, data }) {
            this.value = value;
            this.followRow(data);
        },
        onRowDataChangedDo({ node }) {
            this.followRow(node.data);
        },
        followRow(data) {
            this.editable = data.ratesEditable;
            this.invalid = !!data.invalidRates?.[this.params.colDef.field];
        },
        // The "x" sends null: a cleared rate is blank text. The row handed over is the row as the
        // grid holds it now (its node's data): the params the cell was drawn with keep that time's.
        onInputDo(text) {
            const { context, colDef, node } = this.params;

            this.value = text ?? '';
            context.updateRow(node.data, { [colDef.field]: this.value });
        },
    },
    beforeMount() {
        this.show(this.params);
        this.params.node.addEventListener('dataChanged', this.onRowDataChangedDo);
    },
    beforeUnmount() {
        this.params.node.removeEventListener('dataChanged', this.onRowDataChangedDo);
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';

/*
 * The 40px field of the campaigns forms (form-fields-40px), as wide as its cell (Figma node
 * 8684:6974), in the font of the page's other fields rather than the grid's. A long text stays on
 * one line and scrolls inside the field, with the "x" in view.
 */
.gst-rate-text-cell {
    @include form-fields-40px;

    font-family: $font-family;
}
</style>
